import { useEffect, useState } from 'react';

const ACCESS_RELEASE_MINUTES = 5;
const ACCESS_WATCH_START_MINUTES = 11;
const ACCESS_WATCH_INTERVAL_MS = 15_000;

function isEligibleSessionStatus(status: Sessions.SessionStatus): boolean {
	return status === 'CONFIRMED' || status === 'STARTED';
}

function canUnlockSessionAccess(
	session: Pick<Sessions.Session, 'is_online' | 'selected_date' | 'status'>,
): boolean {
	if (!session.is_online || !isEligibleSessionStatus(session.status)) {
		return false;
	}

	const sessionStartMs = new Date(session.selected_date).getTime();
	if (Number.isNaN(sessionStartMs)) {
		return false;
	}

	return sessionStartMs - Date.now() <= ACCESS_RELEASE_MINUTES * 60_000;
}

export function useUserSessionAccessWindow(
	session: Pick<Sessions.Session, 'is_online' | 'selected_date' | 'status'>,
): boolean {
	const [isUnlocked, setIsUnlocked] = useState(() =>
		canUnlockSessionAccess(session),
	);

	useEffect(() => {
		let intervalId: number | null = null;
		let timeoutId: number | null = null;

		const clearTimers = () => {
			if (intervalId !== null) {
				window.clearInterval(intervalId);
				intervalId = null;
			}
			if (timeoutId !== null) {
				window.clearTimeout(timeoutId);
				timeoutId = null;
			}
		};

		const evaluateAccess = () => {
			const unlocked = canUnlockSessionAccess(session);
			setIsUnlocked(unlocked);
			if (unlocked) {
				clearTimers();
			}
			return unlocked;
		};

		if (!session.is_online || !isEligibleSessionStatus(session.status)) {
			setIsUnlocked(false);
			clearTimers();
			return clearTimers;
		}

		const sessionStartMs = new Date(session.selected_date).getTime();
		if (Number.isNaN(sessionStartMs)) {
			setIsUnlocked(false);
			clearTimers();
			return clearTimers;
		}

		if (evaluateAccess()) {
			return clearTimers;
		}

		const msUntilWatchWindow =
			sessionStartMs - Date.now() - ACCESS_WATCH_START_MINUTES * 60_000;

		const startWatching = () => {
			if (evaluateAccess()) {
				return;
			}

			intervalId = window.setInterval(() => {
				evaluateAccess();
			}, ACCESS_WATCH_INTERVAL_MS);
		};

		if (msUntilWatchWindow > 0) {
			timeoutId = window.setTimeout(() => {
				startWatching();
			}, msUntilWatchWindow);
		} else {
			startWatching();
		}

		return clearTimers;
	}, [session.is_online, session.selected_date, session.status]);

	return isUnlocked;
}
